import { ComponentType } from "react"
import Login from "../pages/Login"
import { SidebarGroup, SidebarLink, SidebarSection, WrapperType } from "../types/routes"
import NotFound from "../pages/NotFound"
import NewTicket from "../pages/NewTicket"
import TicketDetails from "../pages/TicketDetails"
import ListTickets from "../pages/ListTickets"
import ResetPassword from "../pages/ResetPassword"
import Account from "../pages/Account"
import Mfa from "../pages/Mfa"
import HomeRedirect from "../pages/HomeRedirect"

// USER BASED REDIRECTS
export const authRedirectPath = "/login"
export const privateRedirectPath = "/tickets/list"

// TYPES
type SidebarSectionId = keyof typeof sidebarSectionsConfig
type SidebarGroupId = keyof typeof sidebarGroupsConfig
type SidebarOptions = {
  label: string
  section: SidebarSectionId
  group?: SidebarGroupId
  icon?: ComponentType
}
type SectionsConfig = {
  [key in SidebarSectionId]: {
    label?: string
  }
}
type GroupsConfig = {
  [key in SidebarGroupId]: {
    label: string
    icon?: ComponentType
  }
}
type Route = {
  path: string
  component: ComponentType
  wrapper: WrapperType
  sidebarOptions?: SidebarOptions
}

// CONFIGS
export const routes: Route[] = [
  {
    path: "/login",
    component: Login,
    wrapper: "auth"
  },
  {
    path: "/reset-password",
    component: ResetPassword,
    wrapper: "auth"
  },
  {
    path: "/mfa",
    component: Mfa,
    wrapper: "auth"
  },
  {
    path: "/account",
    component: Account,
    wrapper: "private"
  },
  {
    path: "/",
    component: HomeRedirect,
    wrapper: "private"
  },
  {
    path: "/tickets/new",
    component: NewTicket,
    wrapper: "private",
    sidebarOptions: {
      label: "Nuovo ticket",
      section: "main"
    }
  },
  {
    path: "/tickets/list",
    component: ListTickets,
    wrapper: "private",
    sidebarOptions: {
      label: "Tutti i ticket",
      section: "main"
    }
  },
  {
    path: "/tickets/:ticketId",
    component: TicketDetails,
    wrapper: "private"
  },
  {
    path: "*",
    component: NotFound,
    wrapper: "public"
  }
]
export const sidebarSectionsConfig = {
  main: {},
  pages: {
    label: "Pages"
  }
}
export const sidebarGroupsConfig = {
  tickets: {
    label: "Ticket"
  }
}
/*
  Injects types properly, we need both:
  - typed config to achieve stricter type checking inside `getSidebarSections` and `addRoute`
  - untyped config to exctract exact keys for SidebarSectionId and SidebarGroupId
*/
const sectionsConfig = sidebarSectionsConfig as SectionsConfig
const groupsConfig = sidebarGroupsConfig as GroupsConfig

// HELPERS
export const addRoute = (section: (SidebarGroup | SidebarLink)[], route: Route): void => {
  const sidebarOptions = route.sidebarOptions!
  if (sidebarOptions.group) {
    const groupId = sidebarOptions.group
    const group = section.find((item) => (item as SidebarGroup).id === sidebarOptions.group) as SidebarGroup
    if (group) {
      // add route to existing group
      group.children.push({ path: route.path, label: sidebarOptions.label })
    } else {
      // create group and add route as its first child
      section.push({
        id: groupId,
        label: groupsConfig[groupId].label,
        icon: groupsConfig[groupId].icon,
        children: [{ path: route.path, label: sidebarOptions.label }]
      } as SidebarGroup)
    }
  } else {
    // add route as SidebarLink
    section.push({
      path: route.path,
      label: sidebarOptions.label,
      icon: sidebarOptions.icon
    })
  }
}
const getSidebarSections = (): SidebarSection[] => {
  const mapping = routes.reduce((sections, route) => {
    if (route.sidebarOptions) {
      const sectionId = route.sidebarOptions.section
      if (sections[sectionId]) {
        addRoute(sections[sectionId], route)
      } else {
        sections[sectionId] = []
        addRoute(sections[sectionId], route)
      }
    }
    return sections
  }, {} as { [key in SidebarSectionId]: (SidebarGroup | SidebarLink)[] })

  return Object.keys(mapping).map((key: string) => {
    const k = key as SidebarSectionId
    return {
      label: sectionsConfig[k].label,
      items: mapping[k]
    }
  })
}

export const sidebarSections = getSidebarSections()
